import { useQuery } from "@tanstack/react-query";
import { ApiError, AuthExpired, fetchBlobWithAuth, fetchWithAuth } from "./fetcher.js";
import { documentMetadataPath, documentPdfPath, PDF_MEDIA_TYPE } from "./documentRoutes.js";
import type {
  MeResponse,
  OperatorEventsPage,
  OperatorJobDto,
  OperatorJobsPage,
  OperatorTenantsPage,
  OperatorTenantDetailDto,
  OperatorEmailDraftsPage,
  OperatorEmailDraftDetailDto,
  OperatorFailuresPage,
  RenderedDocument,
} from "./types.js";

/** Phase 15 D-URL-01 -- querystring filter shape locked to engine wire format
 *  (Phase 13 D-LIST-01: ?tenant=a,b&status=Issued,Failed&since=...&until=...&after=...).
 *  The SPA passes this querystring through verbatim. */
export interface JobsFilter {
  tenant?: string[];
  status?: string[];
  since?: string;        // ISO date
  until?: string;        // ISO date
  after?: string;        // cursor
}

function buildJobsQuery(filter: JobsFilter): string {
  const params = new URLSearchParams();
  if (filter.tenant && filter.tenant.length > 0) params.set("tenant", filter.tenant.join(","));
  if (filter.status && filter.status.length > 0) params.set("status", filter.status.join(","));
  if (filter.since) params.set("since", filter.since);
  if (filter.until) params.set("until", filter.until);
  if (filter.after) params.set("after", filter.after);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** D-FETCH-01 polling cadence: 4s interval (CON-07 midpoint of 3-5s window),
 *  paused when the tab is hidden (operators routinely have multiple tabs
 *  open per pilot).
 *
 *  EXPORTED (phase 408) so the chat panel rides the same cadence object rather than
 *  copying the number: chat replies are live-ops, not reporting (reportingQueries.ts
 *  deliberately defines its OWN 60s constant because its cadence differs). Two screens
 *  polling "the console's live cadence" must not be able to drift to two numbers. */
export const POLL_OPTIONS = {
  refetchInterval: 4000,
  refetchIntervalInBackground: false,
} as const;

export function useJobs(filter: JobsFilter) {
  return useQuery({
    queryKey: ["jobs", filter] as const,
    queryFn: () => fetchWithAuth<OperatorJobsPage>(`/internal/v0/jobs${buildJobsQuery(filter)}`),
    ...POLL_OPTIONS,
  });
}

export function useJob(jobId: string) {
  return useQuery({
    queryKey: ["job", jobId] as const,
    queryFn: () => fetchWithAuth<OperatorJobDto>(`/internal/v0/jobs/${jobId}`),
    ...POLL_OPTIONS,
  });
}

/**
 * W-5 (events pagination scope note) -- v1.2 walking skeleton bounds
 * `useJobEvents` to the engine's first events page (default page-size).
 * The engine paginates `/internal/v0/jobs/{id}/events?after=<seq>` and
 * returns `next_cursor: long?`; for v1.2 we DO NOT auto-walk subsequent
 * pages on each poll. Implication: jobs whose total event count exceeds
 * the page size will display only the most recent page-size-many events
 * on the timeline. This is acceptable for the walking skeleton (pilot
 * jobs in Sprint 3 are short-lived; per-job event counts stay within the
 * default page size). Full-history merge is deferred to a follow-up phase
 * (Sprint 3.5 or 4) where TanStack Query's `useInfiniteQuery` + cursor
 * merge replaces this single-page fetch. The `LastUpdated` indicator on
 * Plan 04's JobDetailScreen documents this scope to operators implicitly:
 * if older events are missing, the timeline shows the most-recent page.
 *
 * If a pilot Job's event count grows past the page size before Sprint 3.5
 * lands, the walking-skeleton remediation is to bump the engine page size
 * (Plan 13 default; tunable) -- NOT to retro-fit pagination here.
 */
export function useJobEvents(jobId: string) {
  return useQuery({
    queryKey: ["job-events", jobId] as const,
    queryFn: () => fetchWithAuth<OperatorEventsPage>(`/internal/v0/jobs/${jobId}/events`),
    ...POLL_OPTIONS,
  });
}

/** /me -- single-shot, NO polling (operator metadata changes via admin action only). */
export function useMe() {
  return useQuery({
    queryKey: ["me"] as const,
    queryFn: () => fetchWithAuth<MeResponse>("/internal/v0/me"),
    staleTime: 5 * 60_000,         // 5min
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

// ── Phase 16 Plan 06 — Tenants list + detail hooks ─────────────────

/** Phase 16 D-API-01 — query params for GET /internal/v0/tenants (per D-API-01). */
export interface TenantsFilter {
  q?: string;
  emptyChannelsOnly?: boolean;
  after?: string;
}

function buildTenantsQuery(filter: TenantsFilter): string {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.emptyChannelsOnly) params.set("empty_channels_only", "true");
  if (filter.after) params.set("after", filter.after);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * D-FETCH-01 polling hook for GET /internal/v0/tenants.
 * The empty_channels_only query param is implemented by 16-03's endpoint;
 * this hook is a pure consumer - no engine work required.
 */
export function useTenants(filter: TenantsFilter) {
  return useQuery({
    queryKey: ["tenants", filter] as const,
    queryFn: () =>
      fetchWithAuth<OperatorTenantsPage>(
        `/internal/v0/tenants${buildTenantsQuery(filter)}`,
      ),
    ...POLL_OPTIONS,
  });
}

/** D-FETCH-01 polling hook for GET /internal/v0/tenants/:id. */
export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: ["tenant", tenantId] as const,
    queryFn: () =>
      fetchWithAuth<OperatorTenantDetailDto>(
        `/internal/v0/tenants/${tenantId}`,
      ),
    ...POLL_OPTIONS,
  });
}

// ── Phase 17 Plan 09 — Email drafts hooks ───────────────────────────

/** Phase 17 D-DRAFT-03 — query params for GET /internal/v0/email-drafts. */
export interface EmailDraftsFilter {
  tenant?: string;
  status?: string;
  after?: string;
}

function buildEmailDraftsQuery(filter: EmailDraftsFilter): string {
  const params = new URLSearchParams();
  if (filter.tenant) params.set("tenant", filter.tenant);
  if (filter.status) params.set("status", filter.status);
  if (filter.after) params.set("after", filter.after);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * D-FETCH-01 polling hook for GET /internal/v0/email-drafts.
 * 4s polling cadence (D-FETCH-01); pauses when tab is hidden.
 */
export function useEmailDrafts(filter: EmailDraftsFilter) {
  return useQuery({
    queryKey: ["email-drafts", filter] as const,
    queryFn: () =>
      fetchWithAuth<OperatorEmailDraftsPage>(
        `/internal/v0/email-drafts${buildEmailDraftsQuery(filter)}`,
      ),
    ...POLL_OPTIONS,
  });
}

/** D-FETCH-01 polling hook for GET /internal/v0/email-drafts/:id. */
export function useEmailDraft(draftId: string) {
  return useQuery({
    queryKey: ["email-draft", draftId] as const,
    queryFn: () =>
      fetchWithAuth<OperatorEmailDraftDetailDto>(
        `/internal/v0/email-drafts/${draftId}`,
      ),
    ...POLL_OPTIONS,
    enabled: !!draftId,
  });
}

// ── Phase 19 Plan 09 — Failures list hook ─────────────────────────

/** Phase 19 D-FAIL-02 — query params for GET /internal/v0/failures.
 *  Multi-value params use repeated keys (?tenant=A&tenant=B) — NOT comma-joined —
 *  because the engine uses Query["tenant"].ToArray() (Plan 06 endpoint). */
export interface FailuresFilter {
  tenant?: string[];
  reason?: string[];
  since?: string;     // YYYY-MM-DD
  until?: string;     // YYYY-MM-DD
  after?: string;     // pagination cursor
}

function buildFailuresQuery(filter: FailuresFilter): string {
  const params = new URLSearchParams();
  if (filter.tenant && filter.tenant.length > 0) {
    for (const t of filter.tenant) params.append("tenant", t);
  }
  if (filter.reason && filter.reason.length > 0) {
    for (const r of filter.reason) params.append("reason", r);
  }
  if (filter.since) params.set("since", filter.since);
  if (filter.until) params.set("until", filter.until);
  if (filter.after) params.set("after", filter.after);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** D-FETCH-01 polling hook for GET /internal/v0/failures.
 *  Mirrors useJobs + useEmailDrafts: 4s refetchInterval (Phase 15 D-FETCH-01). */
export function useFailures(filter: FailuresFilter) {
  return useQuery({
    queryKey: ["failures", filter] as const,
    queryFn: () =>
      fetchWithAuth<OperatorFailuresPage>(
        `/internal/v0/failures${buildFailuresQuery(filter)}`,
      ),
    ...POLL_OPTIONS,
  });
}

// ── #1969 — rendered documents (the staff document page) ──────────

/**
 * Re-ask once on a transport failure or a 5xx; never on a refusal. A 404 (masked: unknown or
 * another tenant's) and a 403 (role) are answers — asking again cannot change them and only
 * delays the page saying so. An expired session is AuthGate's to handle.
 */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (error instanceof AuthExpired) return false;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * The engine answers `application/pdf`; if a proxy strips the type the browser's viewer would
 * not know what it holds, so the bytes are re-labelled rather than trusted to arrive typed.
 */
export function asPdfBlob(blob: Blob): Blob {
  return blob.type === PDF_MEDIA_TYPE ? blob : new Blob([blob], { type: PDF_MEDIA_TYPE });
}

/**
 * GET /internal/v0/documents/:id — a rendered document's metadata. Modelled on
 * `useEmailDraft`, but it does NOT poll: a rendered document is immutable once stored, so
 * there is nothing to refresh (and so no LastUpdated indicator on its screen).
 */
export function useRenderedDocument(documentId: string) {
  return useQuery({
    queryKey: ["rendered-document", documentId] as const,
    queryFn: () => fetchWithAuth<RenderedDocument>(documentMetadataPath(documentId)),
    enabled: !!documentId,
    staleTime: Infinity,
    refetchInterval: false,
    retry: retryUnlessRefused,
  });
}

/**
 * GET /internal/v0/documents/:id/pdf — the rendered PDF as a Blob, fetched with the staff
 * bearer (`fetchBlobWithAuth`). `enabled` lets the screen wait for the metadata read, so a
 * masked 404 costs one request, not two. Read once per visit: no poll, no refetch on focus
 * (a refetch would hand the viewer a new object URL and reload the PDF under the reader).
 */
export function useRenderedDocumentPdf(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["rendered-document-pdf", documentId] as const,
    queryFn: async () => asPdfBlob(await fetchBlobWithAuth(documentPdfPath(documentId))),
    enabled: !!documentId && enabled,
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: retryUnlessRefused,
  });
}
